import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalNew from "../../ui/popup/Modal";
import PopupHeader from "../../ui/popup/common/popupHeader";
import PopupContent from "../../ui/popup/common/PopupContent";
import SmartButton from "../../ui/buttons/SmartButton";
import LoadingPlaceholder from "../../ui/loading/LoadingPlaceholder";
import { updateFiling, getPreviousExperts, triggerFilingMatching } from "../../../api/filings/filingAPI";
import { Handler401 } from "../../../api/utils/responseActions";
import "../../../styles/v2/components/forms.css";

function FilingAcceptedPayment({ filingUuid }) {
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("payment_confirmed"); // "payment_confirmed" | "showing_recommendation" | "matching"
  const [previousExpert, setPreviousExpert] = useState(null);
  const [checkingExpert, setCheckingExpert] = useState(false);
  const [matching, setMatching] = useState(false);
  const [matchingWithPrevious, setMatchingWithPrevious] = useState(false); // Track if matching with previous expert
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  async function activateFiling() {
    try {
      const apiUrl = `tax/filing`;
      const response = await updateFiling(
        apiUrl,
        { payment_accepted: true },
        filingUuid
      );

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error("Error updating filing status:", error);
      setLoading(false);
    }
  }

  const handleGetMatched = async () => {
    setCheckingExpert(true);
    setError(null);

    try {
      // Expected response: { expert: { id, first_name, last_name, profile_picture, license_type } }
      // license_type should be one of: "CERTIFIED PUBLIC ACCOUNTANT", "ENROLLED AGENT", "TAX ATTORNEY"
      const response = await getPreviousExperts(filingUuid);

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch previous experts");
      }

      const data = await response.json();

      setCheckingExpert(false);

      // If there's a previous expert, show recommendation
      if (data && data.expert) {
        setPreviousExpert(data.expert);
        setStep("showing_recommendation");
      } else {
        // No previous expert, trigger matching immediately
        await startMatching("matching_algorithm");
      }
    } catch (err) {
      console.error("Error fetching previous experts:", err);
      setCheckingExpert(false);
      // On error, trigger regular matching
      await startMatching("matching_algorithm");
    }
  };

  // Start matching process with flexible matching type
  // matchingType: "previous_expert" | "matching_algorithm"
  // expertData: Additional data needed for the matching type (e.g., { expert_id: "123" })
  // Note: Endpoint only needs to return success status - matching happens in background
  const startMatching = async (matchingType = "matching_algorithm", expertData = null) => {
    setMatchingWithPrevious(matchingType === "previous_expert");
    setStep("matching");
    setMatching(true);
    setError(null);

    try {
      // Build matching data based on type
      const matchingData = {
        matching_type: matchingType,
        ...(expertData && { expert_data: expertData })
      };

      const response = await triggerFilingMatching(filingUuid, matchingData);

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to start matching");
      }

      // Success - matching triggered, no response data needed
      setMatching(false);
    } catch (err) {
      console.error("Error starting matching:", err);
      setError("Failed to start matching. Please try again.");
      setMatching(false);
      setStep("showing_recommendation"); // Return to recommendation on error
    }
  };

  const handleGoToDashboard = () => {
    navigate(`/tax-filing/filing/${filingUuid}`);
  };

  // Handler for "Yes, match me with [Name]" button
  // Sends: { matching_type: "previous_expert", expert_data: { expert_id: <id> } }
  const handleMatchWithPrevious = () => {
    if (previousExpert) {
      startMatching("previous_expert", { expert_id: previousExpert.id });
    }
  };

  // Handler for "No, find me someone new" button
  // Sends: { matching_type: "matching_algorithm" }
  const handleMatchWithNew = () => {
    startMatching("matching_algorithm");
  };

  useEffect(() => {
    activateFiling();
  }, [filingUuid, navigate]);

  // Determine modal title and icon based on step
  const getModalTitle = () => {
    switch (step) {
      case "payment_confirmed":
        return "Payment confirmed!";
      case "showing_recommendation":
        return "Matching";
      case "matching":
        return "Matching";
      default:
        return "Payment confirmed!";
    }
  };

  const getModalIcon = () => {
    if (step === "showing_recommendation") {
      return `${process.env.PUBLIC_URL}/images/icons/user_blue_popup.svg`;
    }
    return `${process.env.PUBLIC_URL}/images/icons/survey_blue_popup.svg`;
  };

  return (
    <ModalNew style={{ maxWidth: step === "showing_recommendation" ? "500px" : "450px" }}>
      <PopupHeader
        title={getModalTitle()}
        setOpenState={() => {}}
        hideCloseButton={true}
        icon={getModalIcon()}
        variant="compact"
      />
      <PopupContent>
        {/* Step 1: Payment Confirmed */}
        {step === "payment_confirmed" && (
          loading || checkingExpert ? (
            <LoadingPlaceholder rows={3} type="line" />
          ) : (
            <div className="filing-payment-center">
              <p className="filing-payment-message">
                Your payment has been confirmed. You can now get matched with a CPA to help you with your filing.
              </p>
              <SmartButton
                type="button"
                className="cta-button primary"
                onClick={handleGetMatched}
                text="Get Matched"
              />
            </div>
          )
        )}

        {/* Step 2: Show previous expert recommendation */}
        {step === "showing_recommendation" && previousExpert && (
          <>
            <div className="previous-expert-recommendation">
              <div className="cpa-recommendation-card">
                <div className="cpa-recommendation-header">
                  {previousExpert.profile_picture && (
                    <img
                      src={previousExpert.profile_picture}
                      alt={`${previousExpert.first_name} ${previousExpert.last_name}`}
                      className="cpa-recommendation-avatar"
                    />
                  )}
                  <div className="cpa-recommendation-info">
                    <h3 className="cpa-recommendation-name">
                      {previousExpert.first_name} {previousExpert.last_name}
                    </h3>
                    {previousExpert.license_type && (
                      <div className="cpa-recommendation-license-type">
                        <img
                          src={`${process.env.PUBLIC_URL}/images/icons/verified_blue_badget.svg`}
                          alt="Verified"
                          className="cpa-recommendation-verified-badge"
                        />
                        <span>{previousExpert.license_type}</span>
                      </div>
                    )}
                    <p>
                      Have worked together
                    </p>
                  </div>
                </div>
                <button className="cta-button primary" onClick={handleMatchWithPrevious}>
                  Yes, match me with {previousExpert.first_name}
                </button>
              </div>
            </div>

            {error && (
              <div className="tax-price-demo-error">
                {error}
              </div>
            )}

            <div className="general-matching-recommendation">
              <button className="cta-button secondary" onClick={handleMatchWithNew}>No, find me someone new</button>
            </div>
          </>
        )}

        {/* Step 3: Matching in progress */}
        {step === "matching" && (
          <div className="filing-payment-center">
            {matchingWithPrevious ? (
              /* Matching with previous expert */
              <>
                <div className="matching-avatar-container">
                  {previousExpert?.profile_picture && (
                    <img
                      src={previousExpert.profile_picture}
                      alt={`${previousExpert.first_name} ${previousExpert.last_name}`}
                      className="matching-previous-expert-avatar"
                    />
                  )}
                </div>
                <p className="filing-payment-message">
                  We already notified <strong>{previousExpert?.first_name} {previousExpert?.last_name}</strong>. Please feel free to go to the filing dashboard and start uploading the documents to speed up the process.
                </p>
              </>
            ) : (
              /* Matching with new expert */
              <>
                <div className="matching-animation-container">
                  {/* User avatar */}
                  <div className="matching-avatar-circle matching-avatar-circle-user">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/user_blue_popup.svg`}
                      alt="You"
                      className="matching-avatar-icon"
                    />
                  </div>
                  
                  {/* Animated arrows */}
                  <div className="matching-animated-arrows">
                    ⟷
                  </div>
                  
                  {/* Expert avatar */}
                  <div className="matching-avatar-circle matching-avatar-circle-expert">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/user_blue_popup.svg`}
                      alt="Expert"
                      className="matching-avatar-icon"
                    />
                  </div>
                </div>
                
                <p className="filing-payment-message">
                  We are connecting you with the right expert. This process can take some time, please feel free to go to the filing dashboard and start uploading the documents to speed up the process.
                </p>
              </>
            )}

            {error && (
              <div className="tax-price-demo-error filing-payment-error-spacing">
                {error}
              </div>
            )}

            <SmartButton
              type="button"
              className="cta-button primary"
              onClick={handleGoToDashboard}
              text="Filing Dashboard"
            />
          </div>
        )}
      </PopupContent>
    </ModalNew>
  );
}

export default FilingAcceptedPayment;
